import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { apiRequest } from '../../utils/api';

const MessagesPage = () => {
    const { organizationId } = useParams();
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [filter, setFilter] = useState('all'); // all, new, read, replied
    const [showMobileDetail, setShowMobileDetail] = useState(false);

    useEffect(() => {
        loadMessages();
    }, [organizationId, filter]);

    const loadMessages = async () => {
        setLoading(true);
        try {
            const url = filter === 'all'
                ? `/organizations/${organizationId}/contact`
                : `/organizations/${organizationId}/contact?status=${filter}`;
            const response = await apiRequest(url);
            setMessages(response.data || []);
        } catch (error) {
            console.error('Load messages error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleMarkAsRead = async (id) => {
        try {
            await apiRequest(`/organizations/${organizationId}/contact/${id}/read`, {
                method: 'PUT'
            });
            loadMessages();
            if (selectedMessage?.id === id) {
                setSelectedMessage(prev => ({ ...prev, status: 'read' }));
            }
        } catch (error) {
            console.error('Mark as read error:', error);
        }
    };

    const handleUpdateStatus = async (id, status) => {
        try {
            await apiRequest(`/organizations/${organizationId}/contact/${id}/status`, {
                method: 'PUT',
                body: JSON.stringify({ status })
            });
            loadMessages();
            if (selectedMessage?.id === id) {
                setSelectedMessage(prev => ({ ...prev, status }));
            }
        } catch (error) {
            console.error('Update status error:', error);
        }
    };

    const handleDelete = async (id) => {
        if (!confirm('Bu mesajı silmek istediğinize emin misiniz?')) {
            return;
        }

        try {
            await apiRequest(`/organizations/${organizationId}/contact/${id}`, {
                method: 'DELETE'
            });
            loadMessages();
            if (selectedMessage?.id === id) {
                setSelectedMessage(null);
                setShowMobileDetail(false);
            }
        } catch (error) {
            alert('Mesaj silinirken bir hata oluştu');
        }
    };

    const handleSelectMessage = (message) => {
        console.log('Selected message:', message);
        console.log('IP Address:', message.ip_address);
        console.log('Created at:', message.created_at);
        setSelectedMessage(message);
        setShowMobileDetail(true);
        // Auto mark as read if new
        if (message.status === 'new') {
            handleMarkAsRead(message.id);
        }
    };

    const unreadCount = messages.filter(m => m.status === 'new').length;

    // Format date to relative time
    const formatRelativeTime = (dateStr) => {
        const date = new Date(dateStr);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Şimdi';
        if (diffMins < 60) return `${diffMins} dk önce`;
        if (diffHours < 24) return `${diffHours} sa önce`;
        if (diffDays < 7) return `${diffDays} gün önce`;
        return date.toLocaleDateString('tr-TR', { timeZone: 'Europe/Istanbul' });
    };

    // Format full date time - Turkey timezone using Intl API
    const formatDateTime = (dateStr) => {
        if (!dateStr) return '-';
        try {
            const date = new Date(dateStr);
            // Use Intl.DateTimeFormat for reliable timezone conversion
            const options = {
                timeZone: 'Europe/Istanbul',
                day: 'numeric',
                month: 'long',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            };
            return new Intl.DateTimeFormat('tr-TR', options).format(date);
        } catch (e) {
            console.error('Date format error:', e);
            return dateStr;
        }
    };

    // Format time only - Turkey timezone using Intl API
    const formatTime = (dateStr) => {
        if (!dateStr) return '-';
        try {
            const date = new Date(dateStr);
            const options = {
                timeZone: 'Europe/Istanbul',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            };
            return new Intl.DateTimeFormat('tr-TR', options).format(date);
        } catch (e) {
            console.error('Time format error:', e);
            return dateStr;
        }
    };

    // Get status badge
    const getStatusBadge = (status) => {
        switch (status) {
            case 'new':
                return <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">Yeni</span>;
            case 'read':
                return <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">Okundu</span>;
            case 'replied':
                return <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">Yanıtlandı</span>;
            case 'archived':
                return <span className="px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-full">Arşivlendi</span>;
            default:
                return null;
        }
    };

    // Get initials from name
    const getInitials = (name) => {
        if (!name) return '?';
        const parts = name.trim().split(' ');
        if (parts.length >= 2) {
            return (parts[0][0] + parts[1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    // Get random color for avatar based on name
    const getAvatarColor = (name) => {
        const colors = [
            'bg-gradient-to-br from-blue-500 to-blue-600',
            'bg-gradient-to-br from-purple-500 to-purple-600',
            'bg-gradient-to-br from-pink-500 to-pink-600',
            'bg-gradient-to-br from-orange-500 to-orange-600',
            'bg-gradient-to-br from-green-500 to-green-600',
            'bg-gradient-to-br from-teal-500 to-teal-600',
            'bg-gradient-to-br from-indigo-500 to-indigo-600',
            'bg-gradient-to-br from-red-500 to-red-600',
        ];
        const index = name ? name.charCodeAt(0) % colors.length : 0;
        return colors[index];
    };

    return (
        <AdminLayout>
            <div className="flex h-[calc(100vh-4rem)] bg-gray-100">
                {/* Messages List - Left Panel */}
                <div className={`${showMobileDetail ? 'hidden md:flex' : 'flex'} w-full md:w-96 lg:w-[420px] flex-col bg-white border-r border-gray-200`}>
                    {/* Header */}
                    <div className="p-4 bg-gradient-to-r from-orange-500 to-red-500">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                    </svg>
                                </div>
                                <div>
                                    <h1 className="text-xl font-bold text-white">Mesajlar</h1>
                                    <p className="text-xs text-white/70">{messages.length} mesaj</p>
                                </div>
                            </div>
                            {unreadCount > 0 && (
                                <span className="bg-white text-orange-600 text-sm font-bold px-3 py-1 rounded-full shadow-lg">
                                    {unreadCount} yeni
                                </span>
                            )}
                        </div>
                        
                        {/* Filter Tabs */}
                        <div className="flex gap-2 flex-wrap">
                            {[
                                { value: 'all', label: 'Tümü' },
                                { value: 'new', label: 'Yeni' },
                                { value: 'read', label: 'Okunmuş' },
                                { value: 'replied', label: 'Yanıtlanan' },
                                { value: 'archived', label: 'Arşiv' }
                            ].map(tab => (
                                <button
                                    key={tab.value}
                                    onClick={() => setFilter(tab.value)}
                                    className={`px-3 py-1.5 text-sm font-medium rounded-full transition ${
                                        filter === tab.value
                                            ? 'bg-white text-orange-600'
                                            : 'bg-white/20 text-white hover:bg-white/30'
                                    }`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Messages List */}
                    <div className="flex-1 overflow-y-auto">
                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500"></div>
                            </div>
                        ) : messages.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-16 px-4">
                                <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                                    <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                    </svg>
                                </div>
                                <p className="text-gray-500 font-medium">Mesaj bulunamadı</p>
                                <p className="text-gray-400 text-sm mt-1">Henüz gelen mesaj yok</p>
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-100">
                                {messages.map((message) => (
                                    <div
                                        key={message.id}
                                        onClick={() => handleSelectMessage(message)}
                                        className={`flex items-start gap-3 p-4 cursor-pointer hover:bg-gray-50 transition ${
                                            selectedMessage?.id === message.id ? 'bg-orange-50 border-l-4 border-orange-500' : ''
                                        } ${message.status === 'new' ? 'bg-blue-50/50' : ''}`}
                                    >
                                        {/* Avatar */}
                                        <div className={`w-12 h-12 rounded-full ${getAvatarColor(message.name)} flex items-center justify-center flex-shrink-0 shadow-md`}>
                                            <span className="text-white font-bold text-sm">{getInitials(message.name)}</span>
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between gap-2 mb-1">
                                                <h3 className={`font-semibold truncate ${message.status === 'new' ? 'text-gray-900' : 'text-gray-700'}`}>
                                                    {message.name}
                                                </h3>
                                                <span className="text-xs text-gray-400 whitespace-nowrap">
                                                    {formatRelativeTime(message.created_at)}
                                                </span>
                                            </div>
                                            
                                            {message.phone && (
                                                <p className="text-xs text-gray-500 mb-1">📞 {message.phone}</p>
                                            )}
                                            
                                            <p className={`text-sm line-clamp-2 ${message.status === 'new' ? 'text-gray-700 font-medium' : 'text-gray-500'}`}>
                                                {message.message}
                                            </p>
                                            
                                            <div className="mt-2">
                                                {getStatusBadge(message.status)}
                                            </div>
                                        </div>

                                        {/* Unread indicator */}
                                        {message.status === 'new' && (
                                            <div className="w-3 h-3 rounded-full bg-blue-500 flex-shrink-0 mt-1 animate-pulse"></div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Message Detail - Right Panel */}
                <div className={`${showMobileDetail ? 'flex' : 'hidden md:flex'} flex-1 flex-col bg-gray-50`}>
                    {selectedMessage ? (
                        <>
                            {/* Detail Header */}
                            <div className="bg-white border-b border-gray-200 p-4">
                                <div className="flex items-center gap-4">
                                    {/* Back button - Mobile only */}
                                    <button
                                        onClick={() => setShowMobileDetail(false)}
                                        className="md:hidden w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition"
                                    >
                                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                        </svg>
                                    </button>

                                    {/* Avatar */}
                                    <div className={`w-12 h-12 rounded-full ${getAvatarColor(selectedMessage.name)} flex items-center justify-center shadow-md`}>
                                        <span className="text-white font-bold">{getInitials(selectedMessage.name)}</span>
                                    </div>

                                    {/* Info */}
                                    <div className="flex-1 min-w-0">
                                        <h2 className="text-lg font-bold text-gray-900 truncate">{selectedMessage.name}</h2>
                                        <div className="flex items-center gap-3 text-sm text-gray-500">
                                            {selectedMessage.phone && (
                                                <a href={`tel:${selectedMessage.phone}`} className="hover:text-orange-600 transition">
                                                    📞 {selectedMessage.phone}
                                                </a>
                                            )}
                                            {selectedMessage.email && (
                                                <a href={`mailto:${selectedMessage.email}`} className="hover:text-orange-600 transition truncate">
                                                    ✉️ {selectedMessage.email}
                                                </a>
                                            )}
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-center gap-2">
                                        {selectedMessage.phone && (
                                            <a
                                                href={`tel:${selectedMessage.phone}`}
                                                className="w-10 h-10 rounded-full bg-green-100 text-green-600 flex items-center justify-center hover:bg-green-200 transition"
                                                title="Ara"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                                </svg>
                                            </a>
                                        )}
                                        <button
                                            onClick={() => handleDelete(selectedMessage.id)}
                                            className="w-10 h-10 rounded-full bg-red-100 text-red-600 flex items-center justify-center hover:bg-red-200 transition"
                                            title="Sil"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Chat Area */}
                            <div className="flex-1 overflow-y-auto p-6">
                                {/* Date and Time indicator */}
                                <div className="flex justify-center mb-6">
                                    <span className="px-4 py-1.5 bg-gray-200 text-gray-600 text-xs font-medium rounded-full">
                                        {formatDateTime(selectedMessage.created_at)}
                                    </span>
                                </div>

                                {/* Message Bubble */}
                                <div className="flex gap-3 max-w-2xl">
                                    <div className={`w-10 h-10 rounded-full ${getAvatarColor(selectedMessage.name)} flex items-center justify-center flex-shrink-0 shadow-md`}>
                                        <span className="text-white font-bold text-sm">{getInitials(selectedMessage.name)}</span>
                                    </div>
                                    <div className="flex-1">
                                        <div className="bg-white rounded-2xl rounded-tl-sm shadow-sm p-4 mb-2">
                                            {selectedMessage.subject && (
                                                <p className="text-sm font-semibold text-orange-600 mb-2">
                                                    📋 {selectedMessage.subject}
                                                </p>
                                            )}
                                            <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                                                {selectedMessage.message}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-3 text-xs text-gray-400 px-2">
                                            <span>{formatTime(selectedMessage.created_at)}</span>
                                            <span>• IP: {selectedMessage.ip_address || 'Bilinmiyor'}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Bottom Actions */}
                            <div className="bg-white border-t border-gray-200 p-4">
                                <div className="flex items-center justify-between gap-4">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-gray-500">Durum:</span>
                                        {getStatusBadge(selectedMessage.status)}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {selectedMessage.status !== 'replied' && (
                                            <button
                                                onClick={() => handleUpdateStatus(selectedMessage.id, 'replied')}
                                                className="px-4 py-2 bg-green-500 text-white text-sm font-medium rounded-lg hover:bg-green-600 transition flex items-center gap-2"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                                Yanıtlandı İşaretle
                                            </button>
                                        )}
                                        {selectedMessage.status !== 'archived' && (
                                            <button
                                                onClick={() => handleUpdateStatus(selectedMessage.id, 'archived')}
                                                className="px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300 transition flex items-center gap-2"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                                                </svg>
                                                Arşivle
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        /* Empty State */
                        <div className="flex-1 flex items-center justify-center">
                            <div className="text-center px-4">
                                <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-orange-100 to-red-100 flex items-center justify-center">
                                    <svg className="w-12 h-12 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">Mesaj Seçin</h3>
                                <p className="text-gray-500">
                                    Detayları görmek için sol taraftan bir mesaj seçin
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </AdminLayout>
    );
};

export default MessagesPage;
